/**
 * Inventory Analytics — top consumed / least used / most expensive.
 * Reads /api/inventory/analytics + /api/inventory/dashboard for trend graph.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, IndianRupee, PackageX } from "lucide-react";
import InventoryTabs from "@/components/inventory/InventoryTabs";

export default function InventoryAnalytics() {
  const [days, setDays] = useState(90);
  const [an, setAn] = useState(null);
  const [dash, setDash] = useState(null);
  const [dead, setDead] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, d, ds] = await Promise.all([
          api.get(`/inventory/analytics?days=${days}`),
          api.get("/inventory/dashboard"),
          api.get(`/inventory/reports/dead-stock?days=${days}`),
        ]);
        setAn(a.data); setDash(d.data); setDead(ds.data.rows || []);
      } catch (e) { toast.error(apiError(e, "Failed to load analytics")); }
    })();
  }, [days]);

  const maxTrend = dash?.consumption_trend?.reduce((m, r) => Math.max(m, r.qty), 0) || 1;

  return (
    <div className="max-w-6xl mx-auto py-8 px-6 space-y-4" data-testid="analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline text-ink-soft">Inventory</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Analytics</h1>
          <p className="mt-2 text-sm text-ink-mid">
            Where your capital sits and what's actually moving.
          </p>
        </div>
        <label className="text-xs text-ink-mid inline-flex items-center gap-1">
          Last
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  className="px-2 py-1 rounded-lg border border-[#E8EAE9] bg-white" data-testid="an-days">
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
          </select>
        </label>
      </div>
      <InventoryTabs />

      {/* Consumption trend bars */}
      <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4">
        <div className="text-[11px] uppercase tracking-widest text-ink-soft mb-3">Consumption trend · daily</div>
        {(dash?.consumption_trend || []).length === 0 ? (
          <div className="py-6 text-center text-ink-soft text-sm">No consumption recorded yet.</div>
        ) : (
          <div className="flex items-end gap-0.5 h-32" data-testid="an-trend">
            {dash.consumption_trend.map(d => (
              <div key={d.day} title={`${d.day}: ${d.qty}`} className="flex-1 bg-pine/70 hover:bg-pine rounded-t"
                   style={{ height: `${Math.max(4, (d.qty / maxTrend) * 100)}%` }} />
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <RankList title="Top consumed" icon={TrendingUp}   rows={an?.top_consumed}   unit=""  testid="an-top" />
        <RankList title="Least used"   icon={TrendingDown} rows={an?.least_used}     unit=""  testid="an-least" />
        <RankList title="Most expensive (avg unit cost)" icon={IndianRupee} rows={an?.most_expensive} unit="₹" testid="an-expensive" />
      </div>

      {/* Dead stock */}
      <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-ink-soft">
            <PackageX className="w-3 h-3" /> Dead stock · no movement in {days} days
          </div>
          <div className="text-xs text-ink-mid">{dead.length} products</div>
        </div>
        {dead.length === 0 ? (
          <div className="py-6 text-center text-ink-soft text-sm" data-testid="an-dead-empty">
            No dead stock — everything moved in the last {days} days.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="an-dead-table">
            <thead className="text-left text-ink-soft overline">
              <tr><th className="px-3 py-2">Product</th><th>On hand</th><th>Unit</th><th className="text-right px-3">Capital locked</th></tr>
            </thead>
            <tbody>
              {dead.slice(0, 20).map((r, i) => (
                <tr key={i} className="border-t border-[#F0EEE8]">
                  <td className="px-3 py-2">{r.name}</td>
                  <td>{r.on_hand}</td>
                  <td className="text-ink-mid text-xs">{r.unit}</td>
                  <td className="text-right px-3 font-medium">₹{Number(r.capital_locked).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function RankList({ title, icon: Icon, rows, unit, testid }) {
  const list = rows || [];
  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4" data-testid={testid}>
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-soft mb-3">
        <Icon className="w-3 h-3 text-clay" /> {title}
      </div>
      {list.length === 0 ? (
        <div className="py-4 text-xs text-ink-soft">No data yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {list.map((r, i) => (
            <li key={i} className="flex justify-between text-sm">
              <span className="truncate mr-2">{r.name}</span>
              <span className="text-ink-mid whitespace-nowrap">{unit}{Number(r.value).toLocaleString("en-IN")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
